import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, Loader2, ArrowLeft, Mail, Smartphone, Layers, MonitorPlay, ShieldCheck } from 'lucide-react';
import { PRODUCT_NAME, PRODUCT_TAGLINE, PRODUCT_VERSION, BRAND_LOGO, BRAND_LOGO_GLOW } from '../lib/version';
import usePageTitle from '../hooks/usePageTitle';
import { describeLoginError, legacyLoginErrorMessage, logLoginError } from '../lib/loginError';
import { r16o3UiEnabled } from '../lib/r14Switch';

/*
 * Sign-in for the V3 UI. Accounts are created by an admin (legacy /userDataManagement/ or the
 * /admin/users page) - there is no self-service signup on the Spring server, so the V2 "Sign up"
 * tab is gone rather than pretending. The left panel states what the product does; it makes no
 * claims it cannot back ("99.9% uptime", "trusted by marketers worldwide" were V2 placeholders).
 */
const FEATURES = [
  { icon: Layers, title: 'Slots', text: 'Every Instagram account, its settings, limits and schedule - in one place.' },
  { icon: Smartphone, title: 'Phones', text: 'Real devices, detected by the desktop client on your PC.' },
  { icon: MonitorPlay, title: 'Live monitor', text: 'Watch a phone work, read its log, step in when it needs you.' },
  { icon: ShieldCheck, title: 'Your server', text: 'Runs against your own NecroMancer server. Nothing leaves it.' },
];

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  usePageTitle(mode === 'forgot' ? 'Reset password' : 'Sign in');

  const appName = branding?.appName || PRODUCT_NAME;
  const tagline = branding?.tagline || PRODUCT_TAGLINE;
  const logoUrl = branding?.logoUrl || BRAND_LOGO;

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      if (token) {
        sessionStorage.setItem('supabase_recovery_token', token);
        localStorage.setItem('supabase_recovery_token', token);
        navigate('/reset-password');
        return;
      }
    }
    const storedToken = sessionStorage.getItem('supabase_recovery_token') || localStorage.getItem('supabase_recovery_token');
    if (storedToken) navigate('/reset-password');
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (error) {
      // A network failure is NOT a credential failure. Telling someone to check their password
      // when the server was simply unreachable cost a real debugging session (2026-08-25).
      // r16o3 (future todo 10): a gateway / dev-proxy 502-504 is "unreachable" too, and only a 401 from the
      // sign-in call itself means the password was refused (lib/loginError.js). Switch OFF = the old wording.
      if (r16o3UiEnabled()) {
        const d = describeLoginError(error);
        logLoginError(error, d);
        toast.error(d.message);
      } else {
        toast.error(legacyLoginErrorMessage(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email address first'); return; }
    setIsLoading(true);
    try {
      await authApi.passwordReset(email);
    } catch (error) {
      // Never reveal whether the address exists.
    } finally {
      toast.success('If an account exists for that address, a reset link is on its way.');
      setMode('login');
      setIsLoading(false);
    }
  };

  const inputCls = 'h-11 bg-secondary/60 border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring/60';

  return (
    <div className="min-h-dvh grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-background text-foreground">
      {/* ------------------------------------------------ brand panel (≥ lg) */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 xl:p-16 border-r border-border/60">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary/15 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-[24rem] w-[24rem] rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.35] [background-image:radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:28px_28px]" />
        </div>
        <div className="relative flex items-center gap-3">
          <img src={logoUrl} alt="" className="h-10 w-10 object-contain drop-shadow-[0_4px_14px_hsl(var(--primary)/.5)]" />
          <span className="font-heading text-xl font-bold tracking-tight">{appName}</span>
        </div>
        <div className="relative max-w-xl">
          <img src={BRAND_LOGO_GLOW} alt="" className="h-28 w-28 mb-8 drop-shadow-[0_16px_40px_hsl(var(--primary)/.35)]" />
          <h1 className="text-4xl xl:text-5xl font-heading font-bold tracking-tight text-balance leading-[1.05]">
            The control room for your Instagram fleet.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground text-pretty">{tagline}</p>
          <ul className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex gap-3">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-primary/20">
                  <f.icon className="h-[18px] w-[18px]" />
                </span>
                <span>
                  <span className="block text-sm font-semibold">{f.title}</span>
                  <span className="block text-sm text-muted-foreground text-pretty">{f.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-muted-foreground/70">{appName} · v{PRODUCT_VERSION}</p>
      </section>

      {/* ------------------------------------------------ form */}
      <section className="flex items-center justify-center px-5 py-10 sm:px-8 safe-top safe-bottom">
        <div className="w-full max-w-md animate-fade-up" data-testid="auth-form">
          <div className="lg:hidden mb-8 flex flex-col items-center text-center">
            <img src={BRAND_LOGO_GLOW} alt="" className="h-20 w-20 drop-shadow-[0_12px_30px_hsl(var(--primary)/.4)]" />
            <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight">{appName}</h1>
            <p className="mt-1 text-sm text-muted-foreground text-pretty">{tagline}</p>
          </div>

          <div className="surface p-6 sm:p-8">
            {mode === 'forgot' ? (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <h2 className="font-heading text-xl font-semibold">Reset your password</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Enter your email and we'll send a reset link if an account exists.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input id="reset-email" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} data-testid="reset-email-input" />
                </div>
                <Button type="submit" className="w-full h-11 font-semibold" disabled={isLoading} data-testid="reset-submit-button">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Send reset link
                </Button>
                <Button type="button" variant="ghost" className="w-full tap" onClick={() => setMode('login')}>
                  <ArrowLeft className="h-4 w-4" /> Back to sign in
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <h2 className="font-heading text-xl font-semibold">Sign in</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Use the account your admin created for you.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" inputMode="email" autoComplete="username" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button type="button" onClick={() => setMode('forgot')} className="text-sm text-primary hover:underline underline-offset-4 tap" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input id="login-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className={`${inputCls} pr-11`} data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 tap inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 font-semibold shadow-glow" disabled={isLoading} data-testid="login-submit-button">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                  {isLoading ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            )}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            No account? Ask your administrator - accounts are created on the server, never self-served.
          </p>
        </div>
      </section>
    </div>
  );
}
